// ==============================================
// 原生HTTP请求封装
// 完全由原生App代替H5发送请求，加解密过程对H5透明
// 业务代码使用此模块的上层封装，不直接使用此模块（index.ts）
// ==============================================
import type { HttpRequestOptions, NativeResponse } from './types'
import CallbackManager from './CallbackManager'
import { isNativeAvailable, sendGetRequestFromH5, sendPostRequestFromH5 } from './func'
import { HttpMethod } from './types'

const callbackManager = new CallbackManager()

/**
 * 处理原生返回的请求结果
 * 此函数将暴露给全局，供原生App调用
 * @param callbackId 回调ID
 * @param responseData 响应数据（JSON字符串）
 */
function handleNativeResponse(callbackId: string, responseData: string | null): void {
  // console.log('处理原生响应=======', callbackId, responseData)

  if (!callbackId || !responseData) {
    console.error('处理原生响应失败: 缺少回调ID或响应数据')
    return
  }

  try {
    const parsedData = JSON.parse(responseData) as NativeResponse
    if (parsedData.code !== 200) {
      callbackManager.invoke(
        callbackId,
        { code: parsedData.code, message: parsedData.msg ?? '请求失败' },
        parsedData.data,
      )
    }
    callbackManager.invoke(callbackId, null, parsedData.data)
  } catch (error) {
    console.error('处理原生响应"JSON转换"失败:', error)
    callbackManager.invoke(callbackId, { code: 500, message: '响应数据解析失败' }, null)
  }
}

/**
 * 注册全局回调函数
 * 此函数将暴露给全局，供原生App调用
 * @param callbackId 回调ID
 * @param responseData 响应数据（JSON字符串）
 */
if (typeof window !== 'undefined') {
  window.nativeHttpCallback = handleNativeResponse
}

/**
 * 原生App代替H5发送请求
 * @param options 请求选项
 * @returns 包含响应数据的Promise
 */
export function request<T = unknown>(options: HttpRequestOptions) {
  return new Promise<T>((resolve, reject) => {
    if (!isNativeAvailable()) {
      reject(new Error('原生HTTP桥接不可用'))
      return
    }
    if (!options.url) {
      reject(new Error('请求路径不能为空'))
      return
    }
    try {
      const callbackId = callbackManager.register((error, result) => {
        if (error) {
          reject(new Error(`${String(error.code)}: ${error.message}`))
        } else {
          resolve(result as T)
        }
      })
      if (options.method === HttpMethod.GET) sendGetRequestFromH5(callbackId, options.url)
      else sendPostRequestFromH5(callbackId, options.url, options.data)
    } catch (error) {
      if (error instanceof Error) {
        reject(new Error(`请求发送失败: ${error.message}`))
      } else {
        reject(new Error(`请求发送失败: '未知错误'`))
      }
    }
  })
}
